/**
 * Opens the text-entry Office dialog (term-dialog.html) over the document and
 * resolves with what the user entered, or null when they cancel. REJECTS when
 * the dialog machinery fails (never opened / callback never fired / dead
 * webview) so callers can fall back to the pane form.
 *
 * Shared by every prompt that used to live in the pane: "Add defined term",
 * "Mark text", "Yellow brackets" (single value), plus "Insert cross-reference"
 * and "Insert exhibit" (multi-field). Also used from the ribbon and the keyboard
 * shortcuts, which run in the shared runtime where no pane form exists at all.
 *
 * The form is described by the caller and carried in the URL; the pure URL and
 * message helpers live in dialogPrompt.ts.
 */
import {
  buildDialogUrl,
  buildFieldsDialogUrl,
  readDialogValue,
  readDialogValues,
  DEFAULT_PROMPT,
  type TextDialogPrompt,
  type FieldsDialogSpec,
  type DialogField,
} from './dialogPrompt';

export {
  buildDialogUrl,
  buildFieldsDialogUrl,
  readDialogValue,
  readDialogValues,
  DEFAULT_PROMPT,
};
export type { TextDialogPrompt, FieldsDialogSpec, DialogField };

/** Raw message shape the dialog page sends. */
type DialogMessage = {
  type?: string;
  value?: string;
  term?: string;
  values?: Record<string, string>;
};

/**
 * The dialog plumbing, shared by both prompt flavours: open the window, survive
 * the two ways Office can leave us hanging, and hand each message to `read`.
 *
 * Both watchdogs are load-bearing and were added against observed failures:
 * `displayDialogAsync` can fail to invoke its callback AT ALL on a cold-start
 * webview, and the window can open but never talk (dead webview).
 */
function openDialog<T>(url: string, read: (msg: DialogMessage) => T | null): Promise<T | null> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = (v: T | null) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    const fail = (e: Error) => {
      if (!settled) {
        settled = true;
        reject(e);
      }
    };
    try {
      const outer = setTimeout(() => fail(new Error('dialog did not open')), 3000);
      Office.context.ui.displayDialogAsync(url, { width: 28, height: 22 }, (result) => {
        clearTimeout(outer);
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          fail(new Error(result.error?.message ?? 'dialog failed to open'));
          return;
        }
        const dlg = result.value;
        const inner = setTimeout(() => {
          try { dlg.close(); } catch { /* already closed */ }
          fail(new Error('dialog unresponsive'));
        }, 6000);
        dlg.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          clearTimeout(inner);
          let msg: DialogMessage = {};
          try { msg = JSON.parse((arg as { message: string }).message); } catch { return; }
          if (msg.type === 'ready') return; // handshake only
          try { dlg.close(); } catch { /* already closed */ }
          done(read(msg));
        });
        dlg.addEventHandler(Office.EventType.DialogEventReceived, () => {
          clearTimeout(inner);
          done(null); // user closed via window chrome
        });
      });
    } catch (e) {
      fail(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/** Single-value prompt. */
export function promptTextViaDialog(
  prompt: TextDialogPrompt = DEFAULT_PROMPT,
): Promise<string | null> {
  return openDialog(buildDialogUrl(window.location.origin, prompt), readDialogValue);
}

/**
 * Multi-field prompt. Resolves with a name→value map, or null on cancel.
 *
 * REJECTS when a required field is missing from the reply — which is exactly
 * what a stale cached dialog page produces: it cannot render fields it does not
 * know about, so it answers with a single value. Rejecting routes the caller to
 * its pane-form fallback instead of inserting with a silently empty field.
 */
export async function promptFieldsViaDialog(
  spec: FieldsDialogSpec,
): Promise<Record<string, string> | null> {
  const values = await openDialog(
    buildFieldsDialogUrl(window.location.origin, spec),
    readDialogValues,
  );
  if (values === null) return null;
  const missing = spec.fields
    .filter((f) => f.required !== false && !values[f.name]?.trim())
    .map((f) => f.name);
  if (missing.length) {
    throw new Error(`dialog did not return: ${missing.join(', ')}`);
  }
  return values;
}

/**
 * "Add defined term" flavour — the original entry point, kept so the ribbon and
 * shortcut paths (officeActions.ts) read as before.
 */
export function promptTermViaDialog(): Promise<string | null> {
  return promptTextViaDialog(DEFAULT_PROMPT);
}
